import { createAsyncThunk } from "@reduxjs/toolkit";
import { ACTIONS_PHARMACY_NOTIFICATIONS } from "../../../action-types/actions";
import { APP_PATH } from "../../../api/backendPath";
import axios from "axios";
import { toast } from "../../../components/app-toast";
import { handleAccountDeactivated } from "../../../utils/accountDeactivated";

import i18n from "../../../i18n";
// Create axios instance with auth token
const createAxiosInstance = () => {
  const token = sessionStorage.getItem("usr_1a2b3c");
  const instance = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  // Account deactivated by an admin → show the block modal (once). The gateway
  // returns 403 with error_code "ACCOUNT_DEACTIVATED" for every request once
  // the account is blocked.
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      handleAccountDeactivated(error);
      return Promise.reject(error);
    },
  );

  return instance;
};

// Get pharmacy notifications with pagination and filtering
export const getPharmacyNotifications = createAsyncThunk(
  ACTIONS_PHARMACY_NOTIFICATIONS.get_pharmacy_notifications,
  async (
    { page = 1, limit = 20, status, notification_type } = {},
    { rejectWithValue },
  ) => {
    try {
      const axiosInstance = createAxiosInstance();
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (status) params.append("status", status);
      if (notification_type)
        params.append("notification_type", notification_type);

      const response = await axiosInstance.get(
        `${APP_PATH.getPharmacyNotifications}?${params.toString()}`,
      );

      // Handle different response structures
      if (response.data?.data) {
        return response.data.data;
      } else if (response.data?.notifications) {
        // Fallback for different response structure
        return response.data;
      } else {
        console.warn(
          "getPharmacyNotifications: Unexpected response structure, returning empty data",
        );
        return {
          notifications: [],
          pagination: { total: 0, page: 1, limit: 20, totalPages: 0 },
        };
      }
    } catch (error) {
      console.error("Error fetching pharmacy notifications:", error);

      // Handle different error structures
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        i18n.t("pharmacy.toast.failedToFetchPharmacyNotifications");

      // Show toast notification for user feedback
      toast.error(errorMessage);

      return rejectWithValue({
        message: errorMessage,
        status: error?.response?.status,
      });
    }
  },
);

// Mark pharmacy notification as read
export const markPharmacyNotificationRead = createAsyncThunk(
  ACTIONS_PHARMACY_NOTIFICATIONS.mark_pharmacy_notification_read,
  async (notificationId, { rejectWithValue }) => {
    try {
      const axiosInstance = createAxiosInstance();

      const response = await axiosInstance.put(
        `${APP_PATH.markPharmacyNotificationRead(notificationId)}`,
      );

      // Show success toast
      toast.success(i18n.t("pharmacy.toast.notificationMarkedAsRead"));

      return response.data;
    } catch (error) {
      console.error("Error marking pharmacy notification as read:", error);

      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        i18n.t("pharmacy.toast.failedToMarkPharmacyNotificationAsRead");

      // Show error toast
      toast.error(errorMessage);

      return rejectWithValue({
        message: errorMessage,
        status: error?.response?.status,
      });
    }
  },
);

// Delete pharmacy notification
export const deletePharmacyNotification = createAsyncThunk(
  ACTIONS_PHARMACY_NOTIFICATIONS.delete_pharmacy_notification,
  async (notificationId, { rejectWithValue }) => {
    try {
      const axiosInstance = createAxiosInstance();

      const response = await axiosInstance.delete(
        `${APP_PATH.deletePharmacyNotification(notificationId)}`,
      );

      return response.data;
    } catch (error) {
      console.error("Error deleting pharmacy notification:", error);

      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        i18n.t("pharmacy.toast.failedToDeletePharmacyNotification");

      // Show error toast
      toast.error(errorMessage);

      return rejectWithValue({
        message: errorMessage,
        status: error?.response?.status,
      });
    }
  },
);

// Get pharmacy unread notification count
export const getPharmacyUnreadNotificationCount = createAsyncThunk(
  ACTIONS_PHARMACY_NOTIFICATIONS.get_pharmacy_unread_notification_count,
  async (_, { rejectWithValue }) => {
    try {
      const axiosInstance = createAxiosInstance();

      const response = await axiosInstance.get(
        `${APP_PATH.getPharmacyUnreadNotificationCount}`,
      );

      return response.data;
    } catch (error) {
      console.error("Error getting pharmacy unread notification count:", error);

      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        i18n.t("pharmacy.toast.failedToGetPharmacyUnreadNotificationCount");

      return rejectWithValue({
        message: errorMessage,
        status: error?.response?.status,
      });
    }
  },
);

// Mark all pharmacy notifications as read
export const markAllPharmacyNotificationsRead = createAsyncThunk(
  ACTIONS_PHARMACY_NOTIFICATIONS.mark_all_pharmacy_notifications_read,
  async (_, { rejectWithValue }) => {
    try {
      const axiosInstance = createAxiosInstance();

      const response = await axiosInstance.put(
        `${APP_PATH.markAllPharmacyNotificationsRead}`,
      );

      return response.data;
    } catch (error) {
      console.error("Error marking all pharmacy notifications as read:", error);

      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        i18n.t("pharmacy.toast.failedToMarkAllPharmacyNotificationsAsRead");

      return rejectWithValue({
        message: errorMessage,
        status: error?.response?.status,
      });
    }
  },
);
